import { useTranslation } from 'react-i18next';
import type {
  ChargePointData,
  PricingEntry,
} from '../../types/charge-point.types';
import { formatPrice, getCurrencyDisplay } from '../../utils/price-utils';

interface FeesSectionProps {
  chargePoint: ChargePointData;
}

/**
 * Display additional fees with orange left border
 */
export function FeesSection({ chargePoint }: FeesSectionProps) {
  const { t, i18n } = useTranslation();

  const pricingCollection = chargePoint.pricingCollection;

  // Prefer user pricing over public pricing
  const pricing =
    pricingCollection?.pricingsUser ?? pricingCollection?.pricingsPublic;
  const currency = pricing?.currency ?? chargePoint.currency;

  // Get all pricing entries that are actual additional fees
  // Excludes master pricing, tariffs, and spot pricing (which are part of base price)
  // Only includes 'fee' and 'fixed_min' types which represent true additional charges
  const fees: PricingEntry[] = [];
  if (pricing?.pricingEntries) {
    pricing.pricingEntries.forEach((entry) => {
      if (
        !entry.isMasterPrice &&
        (entry.type === 'fee' || entry.type === 'fixed_min')
      ) {
        fees.push(entry);
      }
    });
  }

  // Don't show section if no fees
  if (fees.length === 0) {
    return null;
  }

  const getFeeTitle = (entry: PricingEntry): string => {
    // Prefer API-provided description (e.g., "Start fee2", "Idle fee")
    if (entry.description) return entry.description;

    // Fall back to translation mapping if additionalFeeType is available
    if (entry.additionalFeeType) {
      const typeMap: Record<string, string> = {
        fixed_fee_starting: t('chargePoint.feeStarting'),
        fixed_fee_charging: t('chargePoint.feeCharging'),
        minute_fee_until_fully_charged: t(
          'chargePoint.feeMinuteUntilFullyCharged'
        ),
        minute_fee_until_cable_unplugged: t(
          'chargePoint.feeMinuteUntilUnplugged'
        ),
        idle_fee_after_fully_charged: t('chargePoint.feeIdleAfterFullyCharged'),
      };
      const mapped = typeMap[entry.additionalFeeType];
      if (mapped) return mapped;
    }

    // Final fallback based on fee type and characteristics
    if (entry.type === 'fee') {
      // One-time fee
      if (entry.startMinAfterCharging && entry.startMinAfterCharging > 0) {
        return t('chargePoint.feeCharging');
      }
      return t('chargePoint.feeStarting');
    } else if (entry.type === 'fixed_min') {
      // Per-minute fee
      if (entry.startMinAfterFullyCharged) {
        return t('chargePoint.feeIdleAfterFullyCharged');
      }
      return t('chargePoint.feeMinuteUntilUnplugged');
    }

    // Absolute fallback
    return t('chargePoint.feeAdditional');
  };

  const getFeeTimingText = (entry: PricingEntry): string | null => {
    // Minutes as "45 min", "2 hours" or "1 hour 59 min" (localized)
    const getTimeText = (minutes: number): string => {
      const hours = Math.floor(minutes / 60);
      const remainingMinutes = minutes % 60;
      return [
        hours > 0 && t('chargePoint.feeTiming.hours', { count: hours }),
        remainingMinutes > 0 &&
          t('chargePoint.feeTiming.minutes', { count: remainingMinutes }),
      ]
        .filter(Boolean)
        .join(' ');
    };

    const timingParts: string[] = [];

    // Check for charging start time
    if (entry.startMinAfterCharging && entry.startMinAfterCharging > 0) {
      timingParts.push(
        t('chargePoint.feeTiming.startsAfterCharging', {
          time: getTimeText(entry.startMinAfterCharging),
        })
      );
    } else if (entry.startMinAfterCharging === 0) {
      timingParts.push(t('chargePoint.feeTiming.startsImmediately'));
    }

    // Check for fully charged start time
    if (
      entry.startMinAfterFullyCharged &&
      entry.startMinAfterFullyCharged > 0
    ) {
      timingParts.push(
        t('chargePoint.feeTiming.startsAfterFullCharge', {
          time: getTimeText(entry.startMinAfterFullyCharged),
        })
      );
    } else if (entry.startMinAfterFullyCharged === 0) {
      timingParts.push(t('chargePoint.feeTiming.startsWhenFullyCharged'));
    }

    // Check for end conditions
    if (entry.endAtFullyCharged) {
      timingParts.push(t('chargePoint.feeTiming.endsWhenFullyCharged'));
    }

    // If we have specific additionalFeeType logic, add context
    if (
      entry.additionalFeeType === 'fixed_fee_starting' &&
      timingParts.length === 0
    ) {
      timingParts.push(t('chargePoint.feeTiming.appliedOnStart'));
    }

    return timingParts.length > 0 ? timingParts.join(' • ') : null;
  };

  const getFeeUnit = (entry: PricingEntry): string => {
    if (entry.type === 'fixed_kwh') return '/kWh';
    if (entry.type === 'fixed_min') return '/min';
    if (entry.type === 'fee') return '';
    return '';
  };

  return (
    <div className="space-y-3">
      <h4 className="font-heading font-semibold text-sm text-ink">
        {t('chargePoint.additionalFees')}
      </h4>

      <div className="border-l-4 border-orange-500 bg-warn/10 p-3 rounded-r-lg space-y-3">
        {fees.map((fee, index) => {
          const timingText = getFeeTimingText(fee);
          return (
            <div key={index} className="space-y-0.5">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-ink">
                  {getFeeTitle(fee)}
                </span>
                <span className="text-sm font-bold text-ink">
                  {formatPrice(fee.amount, currency, i18n.language)}{' '}
                  {getCurrencyDisplay(currency)}
                  {getFeeUnit(fee)}
                </span>
              </div>
              {timingText && <p className="text-xs text-muted">{timingText}</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
